import {
  IonButton,
  IonButtons,
  IonContent,
  IonImg,
  IonInput,
  IonLabel,
  IonText,
  isPlatform,
} from '@ionic/react';
import { DniReader } from '@pepper/capacitor-dni-reader';
import classNames from 'classnames';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { idniecap, PACEHandler } from 'idniecap';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DniImage from '@/assets/imgs/dni-image.webp';
import ENVIRONMENT from '@/environments/environment';
import useModal from '@/hooks/useModal';
import { AnalyticsOrigination, AnalyticsStepNumber } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { EDNIModalRoutes } from '@/interfaces/EDniModalInterface/EDniModalInterface';
import {
  OcrAPIData,
  OcrAvailabilityConfigurationTypes,
  OcrFlowTypes,
  OcrNfcRequest,
  OcrStatus,
} from '@/interfaces/OcrInterface/OcrInterface';
import { saveDniData } from '@/redux/eDniModal/dni/dni';
import { getCountryList } from '@/redux/eDniModal/eDniModalAPI';
import { postOcr, postOcrNfc } from '@/redux/ocr/ocrAPI';
import {
  getTimeResult, registerNFCError, registerNFCSkip, registerNFCSuccess,
} from '@/services/analytics';
import InsightService from '@/services/insights';
import { RootState } from '@/store';
import { retrieveCommercialOffers } from '@/utils/commercialOfferHelper';
import { checkNie, formatName } from '@/utils/dniHelper';

import NFCAnimationModal from '../../NFCAnimationModal/NFCAnimationModal';
import styles from './DniScan.module.scss';
import * as testData from './test.json';

interface DniScanProps {
  sendIFrameUrl: (arg0: string) => void;
  nextStep: (arg0: EDNIModalRoutes) => void;
  handleLoading: (arg0: boolean) => void;
}

export default function DniScan({
  sendIFrameUrl,
  nextStep,
  handleLoading,
}: DniScanProps) {
  const dispatch = useDispatch();
  const product = useSelector((state: RootState) => state.product?.data);
  const simulation = useSelector(
    (state: RootState) => state.loanSimulation.data,
  );

  dayjs.extend(customParseFormat);

  const [can, setCan] = useState<string>('');
  const [canError, setCanError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const ocrRequestData = useRef<OcrAPIData | null>(null);
  const [remainingNFCAttempts, setRemainingNFCAttempts] = useState<number | null>(null);
  const [scanError, setScanError] = useState('');
  const [initTime] = useState<dayjs.Dayjs>(dayjs());
  const initReadingTime = useRef<dayjs.Dayjs | null>(null);
  const merchantInfo = useSelector((state: RootState) => state.merchantData.data);
  const { presentModal: presentNFCAnimationModal } = useModal(NFCAnimationModal);

  const initTestData = async () => {
    const ocrData = await postOcr({
      flow: OcrFlowTypes.ORIGINATION_WITHOUT_PROPOSAL,
      availability_configuration: OcrAvailabilityConfigurationTypes.NFC,
    });
    ocrRequestData.current = ocrData.data;
    listenerScanned(testData);
  };
  const initTestDataNieErrorNationality = async () => {
    const ocrData = await postOcr({
      flow: OcrFlowTypes.ORIGINATION_WITHOUT_PROPOSAL,
      availability_configuration: OcrAvailabilityConfigurationTypes.NFC,
    });
    ocrRequestData.current = ocrData.data;
    const testData2 = {

      address: 'AVDA ALBERT EINSTEIN 2 PBJ - SAN FERNANDO DE HENARES - MADRID',
      birth_date: '1981-09-14',
      card_access_number: '724713',
      client_certificate_status: 'UNKNOWN - UNKNOWN',
      document_id: 'Z6031329K',
      expiration_date: '2029-02-11',
      face_photo: 'iVBORw0KGgoAAAANSUhEUgAAATMAAAF6CAIAAAD6bSitAAAAA',
      gender: 'F',
      last_name: 'TEST TEST',
      name: 'MARIA',
      nationality: '', // Asigna el valor de nacionalidad a nationality si es necesario
      nacionalidad: '',
      root_certificate_status: 'UNKNOWN - UNKNOWN',
      support_number: 'E26838955',
      type: 'NIE',
      nombreMadre: 'MARIA',
      nif: 'Z6031329K',
      nombrePadre: 'FRANCISCO',
      birth_country: 'CA',
    };
    listenerScanned(testData2);
  };
  const initTestDataNieNationality = async () => {
    const ocrData = await postOcr({
      flow: OcrFlowTypes.ORIGINATION_WITHOUT_PROPOSAL,
      availability_configuration: OcrAvailabilityConfigurationTypes.NFC,
    });
    ocrRequestData.current = ocrData.data;
    const testData2 = {

      address: 'UNKNOWN',
      birth_date: '1981-09-14',
      card_access_number: '724713',
      client_certificate_status: 'UNKNOWN - UNKNOWN',
      document_id: 'Z6031329K',
      expiration_date: '2029-02-11',
      face_photo: 'iVBORw0KGgoAAAANSUhEUgAAATMAAAF6CAIAAAD6bSitAAAAA',
      gender: 'F',
      last_name: 'TEST TEST',
      name: 'MARIA',
      nationality: 'Canadá',
      nacionalidad: 'Canadá',
      root_certificate_status: 'UNKNOWN - UNKNOWN',
      support_number: 'E26838955',
      type: 'NIE',
      nombreMadre: 'MARIA',
      nif: 'Z6031329K',
      nombrePadre: 'FRANCISCO',
      birth_country: 'Canadá',
    };
    listenerScanned(testData2);
  };
  const getCountries = async () => {
    try {
      const cList = await getCountryList();
      return cList[0].keys;
    } catch (error) {
      return [];
    }
  };

  const insightsDniData = (datosDNIe: Record<string, any>) => Object.keys(datosDNIe).map((key) => ({ [key]: datosDNIe[key] }));

  const listenerScanned = async (data: any) => {
    setLoading(false);
    handleLoading(true);

    if (ocrRequestData.current) {
      try {
        const countryL = await getCountries();
        let ocrData: OcrNfcRequest;
        if (isPlatform('android')) {
          InsightService.logTrace(
            'scanDNI android',
            data && insightsDniData(data),
          );
          if (isPlatform('android')) {
            if (isPlatform('android') && merchantInfo?.merchant_id) {
              InsightService.logTrace(
                'data merchant',
                insightsDniData(merchantInfo),
              );
            }
          }
          const nationalityCode = countryL.find(
            (key) => key.description_code === data.nationality,
          );

          ocrData = {
            type: data.type,
            name: /* formatName(testName) || */ formatName(data.name),
            birth_date: /* testDATE || */ data.birth_date,
            birth_place: data.birth_place,
            last_name: /* formatName(testSurname) || */ formatName(data.last_name),
            expiration_date: data.expiration_date,
            document_id: /* testDNI || */ data.document_id,
            nationality: nationalityCode?.code || '',
            gender: data.gender,
            support_number: data.support_number,
            face_photo: data.face_photo,
            card_access_number: data.card_access_number,
            address: data.address ? data.address : 'UNKNOWN',
            fathers_name: data.fathers_name,
            mothers_name: data.mothers_name,
            signature_photo: data.signature_photo,
            client_certificate_status: data.client_certificate_status,
            root_certificate_status: data.root_certificate_status,
          };
        } else {
          InsightService.logTrace(
            'scanDNI IOS',
            data && insightsDniData(data),
          );
          if (merchantInfo?.merchant_id) {
            InsightService.logTrace(
              'data merchant',
              insightsDniData(merchantInfo),
            );
          }
          const { datosDNIe } = data;
          const nationalityCode = countryL.find(
            (key) => key.alt_code === datosDNIe.nacionalidad,
          );
          // Temporary for iOS

          ocrData = {
            type: datosDNIe ? (checkNie(datosDNIe?.nif) ? 'NIE' : 'DNI') : '',
            name: /* formatName(testName) || */ formatName(datosDNIe?.nombre) || '',
            birth_date: /* testDATE || */ dayjs(datosDNIe?.fechaNacimiento, 'DD/MM/YYYY').format('YYYY-MM-DD') || '',
            birth_place: datosDNIe?.municipioNacimiento || '',
            last_name: /* formatName(testSurname) || */ formatName(`${datosDNIe?.apellido1} ${datosDNIe?.apellido2}`) || '',
            expiration_date: dayjs(datosDNIe?.fechaValidez, 'DD/MM/YYYY').format('YYYY-MM-DD') || '',
            document_id: /* testDNI || */ datosDNIe?.nif || '',
            nationality: nationalityCode?.code || '',
            gender: datosDNIe?.sexo || '',
            support_number: datosDNIe?.numSoporte || '',
            face_photo: datosDNIe?.imagen || '',
            card_access_number: datosDNIe?.can || can || '',
            address: datosDNIe?.direccion ? `${datosDNIe?.direccion} - ${datosDNIe?.municipioActual} - ${datosDNIe?.provinciaActual}` : 'UNKNOWN',
            fathers_name: datosDNIe?.nombrePadre || '',
            mothers_name: datosDNIe.nombreMadre || '',
            signature_photo: datosDNIe?.firma || '',
            client_certificate_status: datosDNIe?.certificadoAutenticacion?.estado === 0 || datosDNIe?.certificadoAutenticacion?.estado === 2 ? 'GOOD - GOOD' : 'UNKNOWN',
            root_certificate_status: datosDNIe?.certificadoAutenticacion?.estado === 0 || datosDNIe.certificadoAutenticacion?.estado === 2 ? 'GOOD - GOOD' : 'UNKNOWN',
          };
        }
        const successAnalytics = {
          time_elapsed: initTime ? getTimeResult(initTime) : 0,
          time_reading: initReadingTime.current ? getTimeResult(initReadingTime.current) : 0,
          document_type: ocrData?.type || '',
          step_number: AnalyticsStepNumber.Simulation,
        };
        if (!ocrData.nationality && ocrData.type === 'NIE') {
          handleLoading(false);
          hasScanError('Se ha producido un error');
          return;
        }
        registerNFCSuccess({ origin: AnalyticsOrigination.App }, successAnalytics);
        const ocrResponse = await postOcrNfc(
          ocrRequestData.current.ocr_document_id,
          ocrData,
        );
        handleLoading(false);
        if (ocrResponse.data.status !== OcrStatus.VLD) {
          registerNFCError({ origin: AnalyticsOrigination.App }, initReadingTime.current ? getTimeResult(initReadingTime.current) : 0);
          setRemainingNFCAttempts(ocrResponse.data.nfc_attempts_remaining);
          hasScanError('Se ha producido un error');
        } else {
          ocrData.ocr_id = ocrRequestData.current.ocr_document_id;
          ocrData.birth_country = ocrResponse.data.third_party_response.birth_country;
          // console.log('ocrData', ocrData);
          dispatch(saveDniData(ocrData));
          nextStep(EDNIModalRoutes.PERSONAL_DATA);
        }
      } catch (error) {
        handleLoading(false);
        console.log(error);
        nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
      }
    } else {
      handleLoading(false);
      nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
    }
  };

  const hasScanError = (errorText: string) => {
    setScanError(errorText);
    setTimeout(() => {
      setScanError('');
    }, 3000);
  };

  const listenerError = (errorT: string) => {
    setLoading(false);
    registerNFCError({ origin: AnalyticsOrigination.App }, initReadingTime.current ? getTimeResult(initReadingTime.current) : 0);
    InsightService.logTrace('errorScanDNI', [
      { error: errorT },
    ]);
    // Show error message
    if (errorT === 'nfcTagIncorrectCAN') {
      setCanError('El CAN es incorrecto');
    }
    hasScanError('Se ha producido un error durante la lectura');
  };

  const disabledNfcScanButton = () => {
    if (remainingNFCAttempts !== null && remainingNFCAttempts === 0) {
      return true;
    }
    return !can || loading;
  };

  const handleContinue = async () => {
    initReadingTime.current = dayjs();
    try {
      if (isPlatform('android')) {
        const ocrData = await postOcr({
          flow: OcrFlowTypes.ORIGINATION_WITHOUT_PROPOSAL,
          availability_configuration: OcrAvailabilityConfigurationTypes.NFC,
        });
        ocrRequestData.current = ocrData.data;

        setLoading(true);

        let data;
        if (remainingNFCAttempts !== null && remainingNFCAttempts > 0) {
          setRemainingNFCAttempts(remainingNFCAttempts - 1);
          data = await DniReader.scanDocument({ can });
        } else if (remainingNFCAttempts === null) {
          data = await DniReader.scanDocument({ can });
        }

        if (!data) {
          registerNFCError({ origin: AnalyticsOrigination.App }, initReadingTime.current ? getTimeResult(initReadingTime.current) : 0);
          InsightService.logTrace('errorScanDNI', [
            { error: data.error },
          ]);
          setLoading(false);
          hasScanError('Se ha producido un error durante la lectura');
        } else {
          InsightService.logTrace('scanDNI', data.datosDNIe && insightsDniData(data.datosDNIe));
          listenerScanned(data);
        }
      } else if (isPlatform('ios')) {
        const response = await idniecap.configure({
          apiKey: ENVIRONMENT.IDNIECAP_LICENSE,
        });
        if (response) {
          InsightService.logTrace('responseIdnieCap', insightsDniData(response));
        }
        // Temporary for iOS
        const ocrData = await postOcr({
          flow: OcrFlowTypes.ORIGINATION_WITHOUT_PROPOSAL,
          availability_configuration: OcrAvailabilityConfigurationTypes.NFC,
        });
        ocrRequestData.current = ocrData.data;
        setLoading(true);
        const data = await idniecap.readPassport({
          accessKey: can,
          paceKeyReference: PACEHandler.CAN_PACE_KEY_REFERENCE,
          tags: ['DG1', 'DG2', 'DG7', 'DG11', 'DG13'],
          esDNIe: true,
        });
        if (data.error) {
          registerNFCError({ origin: AnalyticsOrigination.App }, initReadingTime.current ? getTimeResult(initReadingTime.current) : 0);
          InsightService.logTrace('errorScanDNI', [
            { error: data.error },
          ]);
          setLoading(false);
          hasScanError('Se ha producido un error durante la lectura');
        } else {
          InsightService.logTrace('scanDNI', data.datosDNIe && insightsDniData(data.datosDNIe));
          listenerScanned(data);
        }
      }
    } catch (error: any) {
      if (isPlatform('android')) {
        listenerError(error.message);
      } else {
        InsightService.logTrace('ErrorScanDNI', [
          { error: JSON.stringify(error) },
        ]);
        registerNFCError({ origin: AnalyticsOrigination.App }, initReadingTime.current ? getTimeResult(initReadingTime.current) : 0);
        nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
        handleLoading(false);
      }
    }
  };

  const handleNFCAnimationModal = () => {
    presentNFCAnimationModal({
      onDismiss: (next: boolean) => {
        if (next) {
          handleContinue();
        }
      },
    });
  };

  const handleSkip = async () => {
    try {
      const skipAnalytics = {
        time_elapsed: initTime ? getTimeResult(initTime) : 0,
        step_number: AnalyticsStepNumber.Simulation,
      };
      registerNFCSkip({ origin: AnalyticsOrigination.App }, skipAnalytics);
      if (simulation && product) {
        const offerS = sessionStorage.getItem('pepperSimulationCommercialOffers');
        if (!offerS) {
          const commercialOffers = await retrieveCommercialOffers();
          if (commercialOffers) {
            sessionStorage.setItem(
              'pepper-commercial-offers-S',
              JSON.stringify(commercialOffers),
            );
          }
        }
        handleLoading(true);
        const leadToken = sessionStorage.getItem('leadToken');
        const amount = simulation.tin === 0 ? simulation.total_cost - simulation.opening_fee : simulation.financed_amount - simulation.opening_fee;
        const continuationUrl = `${ENVIRONMENT.URL_PEPPERONE}/?lead_token=${leadToken}&super_express=true&commercial_offer_id=${simulation?.commercial_offer_id}&amount=${amount}&term=${simulation.term}&product=${product.code}&shared_offers=true&is_app=true`;

        sendIFrameUrl(continuationUrl);
      } else {
        nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
        handleLoading(false);
      }
    } catch (error) {
      nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
      handleLoading(false);
    }
  };

  const changeCanInput = (e: any) => {
    const { value } = e.target;
    if (value) {
      if (/^\d{6}$/.test(value)) {
        setCan(value);
        setCanError('');
      } else {
        setCan('');
        setCanError('El CAN debe tener 6 números');
        e.preventDefault();
      }
    } else {
      setCanError('El CAN debe tener 6 números');
    }
  };

  /*   const [testDNI, settestDNI] = useState('');
    const [testDATE, settestDATE] = useState('');
    const [testName, setTestName] = useState('');
    const [testSurname, setTestSurname] = useState(''); */

  return (
    <IonContent className={styles.modalDniScanContainer}>
      {scanError && <div className={styles.scanErrorMessage}>{scanError}</div>}
      {ENVIRONMENT.ENV === 'development' && <button onClick={initTestData} type="button">Test value</button>}
      {ENVIRONMENT.ENV === 'development' && <div><button onClick={initTestDataNieErrorNationality} type="button"> Test value NIE con error</button></div>}
      {ENVIRONMENT.ENV === 'development' && <button onClick={initTestDataNieNationality} type="button"> Test value NIE sin error</button>}
      <IonText className={styles.modalDniScanTitle}>
        Introduce el CAN del DNI o NIE del cliente
      </IonText>
      <IonLabel className={styles.modalDniScanDescription}>
        Puedes encontrar el identificador CAN en la parte frontal del documento,
        como se muestra en la siguiente imagen:
      </IonLabel>
      <div className={styles.dniScanImgWrapper}>
        <IonImg src={DniImage} />
      </div>
      {/*  {ENVIRONMENT.ENV === 'staging'
        && (
          <IonInput
            label="TEST DNI"
            labelPlacement="stacked"
            onIonInput={(e: any) => settestDNI((e.target.value).toUpperCase())}
            className={styles.dniScanCanInput}
          />
        )}
      {ENVIRONMENT.ENV === 'staging'
        && (
          <IonInput
            label="TEST Fecha de nacimiento YYYY-MM-DD"
            labelPlacement="stacked"
            onIonInput={(e: any) => settestDATE(e.target.value)}
            className={styles.dniScanCanInput}
          />
        )}
      {ENVIRONMENT.ENV === 'staging'
        && (
          <IonInput
            label="Nombre"
            labelPlacement="stacked"
            onIonInput={(e: any) => setTestName(e.target.value)}
            className={styles.dniScanCanInput}
          />
        )}
      {ENVIRONMENT.ENV === 'staging'
        && (
          <IonInput
            label="Apellidos"
            labelPlacement="stacked"
            className={styles.dniScanCanInput}
            onIonInput={(e: any) => setTestSurname(e.target.value)}
          />
        )} */}
      <IonInput
        label="CAN"
        type="tel"
        maxlength={6}
        labelPlacement="stacked"
        className={classNames(styles.dniScanCanInput, canError && 'ion-invalid ', canError === '' && 'ion-valid')}
        onIonInput={changeCanInput} // Usa onIonInput para manejar los cambios en el input
      />
      {canError && <span className={styles.validationError}>{canError}</span>}

      <IonButtons className={styles.dniScanNextBtnWrapper}>

        <IonButton
          disabled={disabledNfcScanButton()}
          className={classNames('nextButton', styles.dniScanNextButton)}
          onClick={handleNFCAnimationModal}
        >
          Leer el chip
        </IonButton>
        <IonButton className={styles.dniScanSkipButton} onClick={handleSkip}>
          Saltar y rellenar manualmente
        </IonButton>
      </IonButtons>
    </IonContent>
  );
}
